import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, DialogTitle, Grid, IconButton, styled } from '@mui/material';
import React, { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { ICustomField, FieldTypeEnum, CustomFieldEnum } from 'wisestep-api';
import { ControlledTextInput, Label, WsButton, WsColors } from 'wisestep-base';

import {
  fieldFormDefaultValues,
  toTitleCaseWithSpaces,
} from '../../features/clientsSettings/helper/ClientSettingsHelper';
import {
  FieldIconMap,
  FieldNameMap,
  fieldTypes,
} from '../../features/invoicing/helper/FieldHelper';
import WsCheckBox from '../WsCheckBox/WsCheckBox';

interface FieldFormData extends Omit<ICustomField, 'options'> {
  options: { value: string }[];
}

export interface BaseFormProps {
  defaultValues?: FieldFormData;
  entityType: CustomFieldEnum;
  title?: string;
}

export const StyledButton = styled(ButtonBase)<{ checked: boolean }>(({ checked }) => ({
  width: '100%',
  height: '6rem',
  alignItems: 'flex-start',
  padding: '1rem 1rem',
  borderRadius: '0.25rem',
  ...(checked && {
    color: WsColors.TheBlues500,
    border: '0.0625rem solid',
  }),
}));

export const WsCustomFieldForm = ({
  defaultValues = fieldFormDefaultValues as FieldFormData,
  entityType,
  title,
}: BaseFormProps) => {
  const form = useFormContext<FieldFormData>();
  const {
    fields: options,
    append,
    remove,
  } = useFieldArray({
    control: form.control,
    name: 'options',
  });
  const selectedType = form.watch('type');
  const label = form.watch('label');
  const edit = !!defaultValues.id;

  useEffect(() => {
    if (!form.formState.dirtyFields.name && !edit) {
      form.setValue(
        'name',
        label &&
          label
            .trim()
            .toLowerCase()
            .replace(/[\s]+/g, '_')
            .replace(/[^\w-]+/g, '')
      );
    }
  }, [edit, form, label]);

  const displayTitle = title || `${toTitleCaseWithSpaces(entityType)} Field`;

  return (
    <>
      <DialogTitle>{displayTitle}</DialogTitle>
      <Grid container direction="row" spacing={4} px={2} alignItems="flex-start">
        <Controller
          name="type"
          control={form.control}
          render={({ field }) => (
            <Grid container alignItems="center" ml={3} mt={3} sx={{ flex: 1 }}>
              {fieldTypes.map((type) => {
                const Icon = FieldIconMap[type];
                const checked = field.value === FieldTypeEnum[type];
                return (
                  <Grid key={type} item sm={4}>
                    <StyledButton
                      onClick={() => {
                        if (!checked) field.onChange(FieldTypeEnum[type]);
                      }}
                      checked={checked}
                    >
                      <Grid alignItems="center" justifyContent="flex-start" spacing={1}>
                        <Icon />
                        <Grid>{FieldNameMap[type]}</Grid>
                      </Grid>
                    </StyledButton>
                  </Grid>
                );
              })}
            </Grid>
          )}
        />
        <Grid item xs={12} md={6} spacing={2} px={2}>
          <Box>
            <ControlledTextInput
              name="label"
              type="text"
              fullWidth
              labelText="Field Name"
              variant="outlined"
              required
            />
          </Box>
          <Box>
            <ControlledTextInput
              name="description"
              type="text"
              fullWidth
              labelText="Field Description"
              variant="outlined"
            />
          </Box>
          <Box>
            <ControlledTextInput
              name="name"
              type="text"
              disabled={edit}
              fullWidth
              labelText="Code"
              variant="outlined"
              required
            />
          </Box>

          <Grid container direction="row" spacing={2}>
            <Grid item>
              <WsCheckBox name="visible" label="Visible" />
            </Grid>
            <Grid item>
              <WsCheckBox name="required" label="Mandatory" />
            </Grid>
            <Grid item>
              <WsCheckBox name="isAdvancedSearchEnabled" label="Advance Search" />
            </Grid>
          </Grid>

          {(selectedType === FieldTypeEnum.Dropdown || selectedType === FieldTypeEnum.Checkbox) && (
            <Box>
              <Label>Options</Label>
              <Grid spacing={1}>
                {options.map((field, index) => (
                  <Grid key={field.id} container direction="row" spacing={1} alignItems="center">
                    <Grid item xs>
                      <ControlledTextInput
                        name={`options.${index}.value`}
                        type="text"
                        fullWidth
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item>
                      <IconButton onClick={() => remove(index)}>
                        <CloseIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
                <WsButton
                  startIcon={<AddIcon />}
                  onClick={() => append({ value: '' })}
                  variant="outlined"
                >
                  Add Option
                </WsButton>
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};
